// The last org chosen, remembered in a cookie (#466 § 6): a bare path (`/`,
// `/fleet`, a sign-in's landing) opens where the person last worked. Written by
// the switcher on a choice and by `<OrgScope/>` on every org page that opened,
// so an explicit `/org/<login>` link both wins over it and refreshes it.
//
// A cookie, not `localStorage`: it is per-browser state the server may read one
// day for a server-side landing, and it is the decision of record's word.
// HttpOnly is impossible (JS writes it) and unnecessary: it names an org login,
// never a credential, and the server re-checks membership on every request.
//
// Every access sits behind try/catch: a sandboxed frame or a browser with
// cookies off throws on `document.cookie`, and a lost preference must degrade
// to today's landing, never take the page down.

import { findMembership, isReachable, membershipSegment } from './memberships';
import type { MembershipView } from './session';

export const LAST_ORG_COOKIE = 'dz_last_org';

/** One year, in seconds — a preference, renewed on every org page visited. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** The org login the cookie names, or `null` (absent, blank, or unreadable). */
export function readLastOrg(doc: Pick<Document, 'cookie'> = document): string | null {
  try {
    for (const part of doc.cookie.split(';')) {
      const eq = part.indexOf('=');
      if (eq === -1 || part.slice(0, eq).trim() !== LAST_ORG_COOKIE) continue;
      const value = decodeURIComponent(part.slice(eq + 1).trim());
      return value === '' ? null : value;
    }
  } catch {
    // Cookies unavailable, or a malformed escape: no preference.
  }
  return null;
}

/** Remember `org` as the last one chosen. Never throws. */
export function writeLastOrg(
  org: string,
  doc: Pick<Document, 'cookie'> = document,
  protocol: string = window.location.protocol,
): void {
  const secure = protocol === 'https:' ? '; Secure' : '';
  try {
    doc.cookie = `${LAST_ORG_COOKIE}=${encodeURIComponent(org)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Cookies unavailable: the landing falls back to the session's account.
  }
}

/**
 * The org a bare path should land in by the cookie: its membership's URL
 * segment when the cookie names an org this user belongs to AND this session can
 * open, else `null` (no cookie, a stale org, or one behind another sign-in —
 * landing there would only bounce off `<OrgDenied/>`).
 */
export function lastOrgLanding(
  memberships: readonly MembershipView[],
  cookie: string | null,
): string | null {
  if (cookie === null) return null;
  const m = findMembership(memberships, cookie);
  return m !== undefined && isReachable(m) ? membershipSegment(m) : null;
}
